import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { LandingTemplateList } from "@/components/settings/LandingTemplateList";
import { PreviewDialog } from "@/components/settings/PreviewDialog";
import { TelegramWorkspace } from "@/components/settings/TelegramWorkspace";
import { SettingsContextHeader, SettingsNavigation } from "@/components/settings/SettingsNavigation";
import { SettingsSaveDock } from "@/components/settings/SettingsSaveDock";
import "@/styles/settings-redesign.css";
import {
  ExternalLink,
  Shield,
  Key,
  Save,
  Eye,
  EyeOff,
  Smartphone,
  Check,
  X,
  QrCode,
  Copy,
  Layout,
  FileText,
  Zap,
  Clock,
  Server,
  Users,
  AlertTriangle,
  RefreshCw,
  Activity,
  User,
  Mail,
  Search,
  Lock,
  Globe,
  Monitor,
  Map,
  Plus,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const Settings = () => {
  const { token, user, setUser } = useAuth();
  const isLicensed = user?.is_licensed || user?.role === "super_admin";
  const [activeTab, setActiveTab] = useState("account"); // account, general, security, domains
  // Deep-link support: /settings?tab=domains opens the right tab (e.g. from BYOD banner)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    const allowed = ["account", "general", "security", "advanced"];
    if (t && allowed.includes(t)) {
      const locked = t !== "account" && !isLicensed;
      if (!locked) setActiveTab(t);
    }
  }, [isLicensed]);

  const [settings, setSettings] = useState({
    redirect_url: "",
    redirect_urls: [""],
    current_preview: "blank",
    link_expiry_hours: 0,  // 0 = never expires
    telegram: { enabled: false, bot_token: "", chat_id: "" }
  });
  const [multiUrlEnabled, setMultiUrlEnabled] = useState(false);
  const [maxRedirectUrls, setMaxRedirectUrls] = useState(3);
  const [securitySettings, setSecuritySettings] = useState({
    block_scanners: true,
    block_datacenter_ips: true,
    delayed_redirect: false,  // Disabled - landing pages have built-in delay
    delay_seconds: 3,
    require_referrer: false,
    enable_captcha: false,
    bot_redirect_url: ""
  });
  const [rotationSettings, setRotationSettings] = useState({
    enabled: true,
    mode: "smart",
    max_clicks_per_domain: 100,
    cooldown_hours: 24,
    auto_disable_burned: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBotToken, setShowBotToken] = useState(false);
  const [savedTelegramConfig, setSavedTelegramConfig] = useState({ enabled: false, hasToken: false, chatId: "" });
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramTestMessage, setTelegramTestMessage] = useState({ type: "", text: "" });
  const [settingsMessage, setSettingsMessage] = useState({ type: "", text: "" });
  
  // Profile State
  const [profile, setProfile] = useState({ telegram_id: "" });
  const [savedTelegramId, setSavedTelegramId] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState({ type: "", text: "" });
  
  // 2FA State
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState("");

  // Change Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [landingPreview, setLandingPreview] = useState({
    open: false,
    template: null,
    html: "",
    loading: false,
    error: ""
  });
  const [landingTemplateMessage, setLandingTemplateMessage] = useState({ type: "", text: "" });
  const [savedLandingTemplateId, setSavedLandingTemplateId] = useState("blank");

  // Advanced Security State
  const [advancedSecurity, setAdvancedSecurity] = useState({
    // Click Velocity
    click_velocity_enabled: false,
    max_clicks_per_minute: 10,
    max_clicks_per_hour: 100,
    velocity_action: "block",
    // VPN/Proxy Detection
    vpn_detection_enabled: false,
    block_vpn: true,
    block_proxy: true,
    block_tor: true,
    block_datacenter: true,
    // Browser Fingerprint
    fingerprint_blocking_enabled: false,
    block_headless: true,
    block_automation: true,
    block_inconsistent_fp: true,
    // Geo Anomaly
    geo_anomaly_enabled: false,
    impossible_travel_threshold: 500,
    require_consistent_country: false,
    // TLS/JA3
    tls_fingerprinting_enabled: false,
    block_known_bot_ja3: true,
    block_suspicious_tls: true,
    // Honeypot
    honeypot_enabled: false,
    honeypot_auto_block: true,
    // Reputation
    reputation_scoring_enabled: false,
    reputation_threshold: 30,
    // Time Delay
    time_delay_enabled: false,
    delay_minutes: 5,
    // Scanner Detection
    scanner_detection_enabled: true,
    block_security_scanners: true,
    block_seo_bots: true,
    // Session 10 — Advanced Bot Defense
    enable_ja3: false,
    ja3_denylist: [],
    ja4_denylist: [],
    enable_behavioral: false,
    beh_fail_threshold: 45,
    enable_header_checks: false,
    enable_crawler_cidr: false,
    enable_fp_velocity: false,
    enable_geo_consistency: false,
    geo_block_utc: true,
    second_hop_cloak: false,
    passive_block_threshold: 60
  });
  const [advancedSecuritySaving, setAdvancedSecuritySaving] = useState(false);
  const [advancedSecurityMessage, setAdvancedSecurityMessage] = useState({ type: "", text: "" });
  
  // Device Fingerprint Stats
  const [fingerprintStats, setFingerprintStats] = useState({
    unique: 0,
    returning: 0,
    suspicious: 0
  });

  // Landing page templates from COMCAST server (/root/Redirector/var/www/landing/path/)
  const landingPageTemplates = [
    { id: "blank", name: "Blank", description: "Empty redirect page", icon: "⬜" },
    { id: "adobe", name: "Adobe", description: "Adobe services page", icon: "🎨" },
    { id: "authenticator", name: "Authenticator", description: "2FA verification page", icon: "🔐" },
    { id: "cloudflare", name: "Cloudflare", description: "CDN protection page", icon: "☁️" },
    { id: "custom", name: "Custom", description: "Your custom landing page", icon: "⚙️", category: "custom" },
    { id: "docusign", name: "DocuSign", description: "Document signing page", icon: "✍️" },
    { id: "dropbox", name: "Dropbox", description: "Cloud storage page", icon: "📦" },
    { id: "facebook", name: "Facebook", description: "Social media page", icon: "👥" },
    { id: "google", name: "Google", description: "Google services page", icon: "🔍" },
    { id: "googledrive", name: "Google Drive", description: "Google Drive file sharing", icon: "☁️" },
    { id: "google-invite", name: "Google Invite", description: "Google Calendar meeting invite", icon: "📅" },
    { id: "lock", name: "Lock Screen", description: "Security lock page", icon: "🔒" },
    { id: "new", name: "New Custom", description: "Your alternate custom template", icon: "✨", category: "custom" },
    { id: "office", name: "Office", description: "Office 365 page", icon: "📊" },
    { id: "outlook", name: "Outlook", description: "Email access page", icon: "📧" },
    { id: "ringcentral", name: "RingCentral", description: "Communication page", icon: "📞" },
    { id: "secureportal", name: "Secure Portal", description: "Security portal page", icon: "🛡️" },
    { id: "sharepoint", name: "SharePoint", description: "File sharing page", icon: "📁" },
    { id: "sharepointcover", name: "SharePoint Cover", description: "SharePoint cover page", icon: "📂" },
    { id: "teams", name: "Teams", description: "Teams meeting page", icon: "💬" },
    { id: "zoom", name: "Zoom", description: "Video meeting page", icon: "🎥" }
  ];

  const fetchSettings = useCallback(async () => {
    if (!isLicensed) {
      try {
        const profileRes = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const persistedTelegramId = profileRes.data.telegram_id || "";
        setProfile({ telegram_id: persistedTelegramId });
        setSavedTelegramId(persistedTelegramId);
      } catch (error) {
        console.error("Error fetching account profile:", error);
      }
      setLoading(false);
      return;
    }

    try {
      const [settingsRes, securityRes, rotationRes, profileRes, advSecRes, fpStatsRes] = await Promise.all([
        axios.get(`${API}/user/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/settings/security`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/settings/rotation`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/user/profile`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/user/advanced-security`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/user/fingerprint-stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setSavedLandingTemplateId(settingsRes.data?.current_preview || "blank");
      setSettings(prev => {
        const fetched = settingsRes.data || {};
        const urls = Array.isArray(fetched.redirect_urls) && fetched.redirect_urls.length > 0
          ? fetched.redirect_urls
          : (fetched.redirect_url ? [fetched.redirect_url] : [""]);
        return {
          ...prev,
          ...fetched,
          redirect_url: fetched.redirect_url || urls[0] || "",
          redirect_urls: urls,
        };
      });
      setMultiUrlEnabled(!!settingsRes.data?.multi_destination_urls_enabled);
      setMaxRedirectUrls(settingsRes.data?.max_redirect_urls || 3);
      const persistedBot = settingsRes.data?.telegram || {};
      setSavedTelegramConfig({
        enabled: persistedBot.enabled === true,
        hasToken: Boolean((persistedBot.bot_token || "").trim()),
        chatId: (persistedBot.chat_id || "").trim(),
      });
      if (securityRes.data) setSecuritySettings(securityRes.data);
      if (rotationRes.data) setRotationSettings(rotationRes.data);
      const persistedTelegramId = profileRes.data?.telegram_id || "";
      setProfile({ telegram_id: persistedTelegramId });
      setSavedTelegramId(persistedTelegramId);
      if (advSecRes.data) setAdvancedSecurity(prev => ({ ...prev, ...advSecRes.data }));
      if (fpStatsRes.data) setFingerprintStats(fpStatsRes.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
    setLoading(false);
  }, [isLicensed, token]);

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMessage({ type: "", text: "" });
    const telegramId = profile.telegram_id.trim();
    try {
      const response = await axios.put(`${API}/user/profile`, {
        telegram_id: telegramId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the user context with new data
      if (response.data.user && setUser) {
        setUser(response.data.user);
      }
      setProfile({ telegram_id: telegramId });
      setSavedTelegramId(telegramId);
      setProfileMessage({ type: "success", text: "Profile updated successfully!" });
      setTimeout(() => setProfileMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      setProfileMessage({ 
        type: "error", 
        text: error.response?.data?.detail || "Failed to update profile" 
      });
    }
    setProfileSaving(false);
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    setSettingsMessage({ type: "", text: "" });
    try {
      // Always send redirect_urls array (backend keeps redirect_url in sync from urls[0])
      const cleanedUrls = (settings.redirect_urls || [])
        .map((u) => (u || "").trim())
        .filter((u) => u.length > 0);
      const response = await axios.put(`${API}/user/settings`, {
        redirect_urls: cleanedUrls,
        current_preview: settings.current_preview,
        link_expiry_hours: settings.link_expiry_hours,
        telegram_enabled: settings.telegram.enabled,
        telegram_bot_token: settings.telegram.bot_token,
        telegram_chat_id: settings.telegram.chat_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedLinks = response.data?.links_updated ?? response.data?.inherited_links_updated ?? 0;
      const savedPreview = response.data?.config?.current_preview || settings.current_preview;
      setSavedLandingTemplateId(savedPreview);
      const savedTemplate = landingPageTemplates.find((template) => template.id === savedPreview);
      setLandingTemplateMessage({
        type: "success",
        text: `${savedTemplate?.name || "Landing page"} is active and syncing to your domains.`,
      });
      setSettingsMessage({
        type: "success",
        text: updatedLinks > 0
          ? `Settings saved. All ${updatedLinks} existing campaign link${updatedLinks === 1 ? "" : "s"} now use the new destination.`
          : "Settings saved and synced to agent!"
      });
      setTimeout(() => setSettingsMessage({ type: "", text: "" }), 4000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setSettingsMessage({ type: "error", text: error.response?.data?.detail || "Failed to save settings" });
      setLandingTemplateMessage({ type: "error", text: error.response?.data?.detail || "Failed to activate the selected landing page." });
    }
    setSaving(false);
  };

  const saveTelegramSettings = async () => {
    setSaving(true);
    setSettingsMessage({ type: "", text: "" });
    try {
      await axios.put(`${API}/user/settings`, {
        telegram_enabled: settings.telegram.enabled,
        telegram_bot_token: settings.telegram.bot_token,
        telegram_chat_id: settings.telegram.chat_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedTelegramConfig({
        enabled: settings.telegram.enabled === true,
        hasToken: Boolean((settings.telegram.bot_token || "").trim()),
        chatId: (settings.telegram.chat_id || "").trim(),
      });
      setSettingsMessage({ type: "success", text: "Bot delivery settings saved." });
      setTimeout(() => setSettingsMessage({ type: "", text: "" }), 4000);
    } catch (error) {
      setSettingsMessage({ type: "error", text: error.response?.data?.detail || "Failed to save bot delivery settings" });
    }
    setSaving(false);
  };

  const testTelegramConnection = async () => {
    setTelegramTesting(true);
    setTelegramTestMessage({ type: "", text: "" });
    try {
      const response = await axios.post(`${API}/user/settings/telegram/test`, {
        bot_token: settings.telegram.bot_token,
        chat_id: settings.telegram.chat_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramTestMessage({ type: "success", text: response.data?.message || "Test message delivered." });
    } catch (error) {
      setTelegramTestMessage({ type: "error", text: error.response?.data?.detail || "Telegram test failed" });
    }
    setTelegramTesting(false);
  };

  const selectLandingTemplate = (templateId) => {
    if (!templateId || templateId === settings.current_preview || saving) return;
    setSettings((current) => ({ ...current, current_preview: templateId }));
    const selected = landingPageTemplates.find((template) => template.id === templateId);
    setLandingTemplateMessage(
      templateId === savedLandingTemplateId
        ? { type: "", text: "" }
        : { type: "pending", text: `${selected?.name || "Template"} selected. Use Save Settings to activate it.` }
    );
  };

  const previewLandingTemplate = async (template) => {
    setLandingPreview({ open: true, template, html: "", loading: true, error: "" });
    try {
      const response = await axios.get(`${API}/user/landing-preview/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLandingPreview({
        open: true,
        template,
        html: response.data?.html || "",
        loading: false,
        error: response.data?.html ? "" : "This preview is empty."
      });
    } catch (error) {
      setLandingPreview({
        open: true,
        template,
        html: "",
        loading: false,
        error: error.response?.data?.detail || "Preview is temporarily unavailable."
      });
    }
  };


  const saveSecuritySettings = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/user/settings/security`, securitySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state with saved values
      if (response.data?.settings) {
        setSecuritySettings(prev => ({ ...prev, ...response.data.settings }));
      }
      alert("Security settings saved!");
    } catch (error) {
      console.error("Error saving security settings:", error);
      alert("Failed to save security settings");
    }
    setSaving(false);
  };

  const saveRotationSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/rotation`, rotationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Rotation settings saved!");
    } catch (error) {
      console.error("Error saving rotation settings:", error);
      alert("Failed to save rotation settings");
    }
    setSaving(false);
  };

  const saveAdvancedSecurity = async () => {
    setAdvancedSecuritySaving(true);
    setAdvancedSecurityMessage({ type: "", text: "" });
    try {
      const response = await axios.put(`${API}/user/advanced-security`, advancedSecurity, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state with saved values from server
      if (response.data?.advanced_security) {
        setAdvancedSecurity(prev => ({ ...prev, ...response.data.advanced_security }));
      }
      await forcePushSettings();
      setAdvancedSecurityMessage({ type: "success", text: "Advanced security settings saved and pushed to agents!" });
      setTimeout(() => setAdvancedSecurityMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error saving advanced security settings:", error);
      setAdvancedSecurityMessage({ 
        type: "error", 
        text: error.response?.data?.detail || "Failed to save advanced security settings" 
      });
    }
    setAdvancedSecuritySaving(false);
  };

  const forcePushSettings = async () => {
    setAdvancedSecurityMessage({ type: "", text: "" });
    try {
      const response = await axios.post(`${API}/user/advanced-security/force-push`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdvancedSecurityMessage({ type: "success", text: `Force push complete! VPN toggle state: ${response.data?.advanced_security_state?.vpn_detection_enabled ? "ON" : "OFF"}` });
      setTimeout(() => setAdvancedSecurityMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      setAdvancedSecurityMessage({ type: "error", text: "Force push failed: " + (error.response?.data?.detail || error.message) });
    }
  };

  const resetDomainUsage = async (domainId) => {
    try {
      await axios.post(`${API}/domains/${domainId}/reset-usage`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchSettings(); // Refresh data
      alert("Domain usage reset!");
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to reset domain");
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case "healthy": return "text-green-400 bg-green-500/10";
      case "moderate": return "text-yellow-400 bg-yellow-500/10";
      case "warning": return "text-orange-400 bg-orange-500/10";
      case "critical": return "text-red-400 bg-red-500/10";
      default: return "text-slate-400 bg-slate-500/10";
    }
  };

  // 2FA Functions
  const setup2FA = async () => {
    setTwoFALoading(true);
    setTwoFAError("");
    try {
      const response = await axios.post(`${API}/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFASetup(response.data);
    } catch (error) {
      setTwoFAError(error.response?.data?.detail || "Failed to setup 2FA");
    }
    setTwoFALoading(false);
  };

  const verify2FA = async () => {
    setTwoFALoading(true);
    setTwoFAError("");
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: twoFACode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFASetup(null);
      setTwoFACode("");
      // Refresh user data
      window.location.reload();
    } catch (error) {
      setTwoFAError(error.response?.data?.detail || "Invalid code");
    }
    setTwoFALoading(false);
  };

  const disable2FA = async () => {
    setTwoFALoading(true);
    setTwoFAError("");
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: twoFACode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFACode("");
      window.location.reload();
    } catch (error) {
      setTwoFAError(error.response?.data?.detail || "Invalid code");
    }
    setTwoFALoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Change Password Function
  const changePassword = async () => {
    setPasswordMessage({ type: "", text: "" });
    
    // Validation
    if (!passwordData.currentPassword) {
      setPasswordMessage({ type: "error", text: "Current password is required" });
      return;
    }
    if (!passwordData.newPassword) {
      setPasswordMessage({ type: "error", text: "New password is required" });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      setPasswordMessage({ type: "error", text: "New password must be at least 8 characters" });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: "error", text: "Passwords do not match" });
      return;
    }
    
    setPasswordSaving(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPasswordMessage({ type: "success", text: "Password changed successfully!" });
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      setPasswordMessage({ 
        type: "error", 
        text: error.response?.data?.detail || "Failed to change password" 
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="settings-redesign" data-testid="settings-redesign-shell">
      <div className="settings-page-header">
        <div>
          <p className="text-xs font-semibold uppercase text-cyan-400">Control center</p>
          <h1 className="mt-1 text-3xl font-orbitron font-bold text-white" data-testid="settings-title">Settings</h1>
          <p className="mt-1 text-sm text-slate-400">Manage account access, routing, and redirect-agent defenses.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="settings-header-summary">
          <span className="rounded-full border border-slate-700 bg-slate-900/70 px-3 py-1.5 text-xs text-slate-400">4 areas</span>
          <span className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${isLicensed ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
            {isLicensed ? "Licensed" : "Account only"}
          </span>
        </div>
      </div>

      <div className="settings-layout">
        <SettingsNavigation activeTab={activeTab} isLicensed={isLicensed} onChange={setActiveTab} />
        <section className="min-w-0" data-testid="settings-active-panel">
          <SettingsContextHeader activeTab={activeTab} isLicensed={isLicensed} />
          <div className="settings-content">

      {/* Account Tab */}
      {activeTab === "account" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile Information */}
          <div className="card-dark p-5 sm:p-6 lg:col-span-2" data-testid="profile-info">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-cyan-400" />
              Profile Information
            </h3>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="settings-profile-fact">
                <p className="text-xs text-slate-500 mb-1">Username</p>
                <p className="text-white font-medium" data-testid="profile-username">{user?.username}</p>
              </div>

              <div className="settings-profile-fact">
                <p className="text-xs text-slate-500 mb-1">Account Status</p>
                <div className="flex items-center gap-2" data-testid="profile-license-status">
                  {user?.is_licensed ? (
                    <span className="flex items-center gap-1 text-green-400">
                      <Check className="w-4 h-4" />
                      Licensed
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-yellow-400">
                      <Clock className="w-4 h-4" />
                      Pending License
                    </span>
                  )}
                </div>
              </div>

              <div className="settings-profile-fact">
                <p className="text-xs text-slate-500 mb-1">Account Created</p>
                <p className="text-slate-300" data-testid="profile-created-date">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</p>
              </div>
            </div>
          </div>

          <TelegramWorkspace
            botAvailable={isLicensed}
            profile={profile}
            profileMessage={profileMessage}
            profileSaving={profileSaving}
            savedTelegramConfig={savedTelegramConfig}
            savedTelegramId={savedTelegramId}
            saving={saving}
            settingsMessage={settingsMessage}
            showBotToken={showBotToken}
            telegram={settings.telegram}
            telegramTesting={telegramTesting}
            telegramTestMessage={telegramTestMessage}
            onAccountChatIdChange={(value) => setProfile({ ...profile, telegram_id: value.replace(/[^0-9-]/g, "") })}
            onBotChatIdChange={(value) => {
              setSettings({ ...settings, telegram: { ...settings.telegram, chat_id: value } });
              setTelegramTestMessage({ type: "", text: "" });
            }}
            onBotTokenChange={(value) => {
              setSettings({ ...settings, telegram: { ...settings.telegram, bot_token: value } });
              setTelegramTestMessage({ type: "", text: "" });
            }}
            onSaveProfile={saveProfile}
            onSaveTelegram={saveTelegramSettings}
            onTestTelegram={testTelegramConnection}
            onToggleEnabled={() => setSettings({ ...settings, telegram: { ...settings.telegram, enabled: !settings.telegram.enabled } })}
            onToggleTokenVisibility={() => setShowBotToken(!showBotToken)}
          />

          {/* 2FA Settings */}
          <div className="card-dark p-5 sm:p-6" data-testid="2fa-settings">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              Two-Factor Authentication
            </h3>

            {user?.two_fa_enabled ? (
              <div className="space-y-4 max-w-md">
                <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="2fa-enabled-status">
                  <Check className="w-5 h-5 text-green-400" />
                  <span className="text-green-400">2FA is enabled</span>
                </div>

                <div>
                  <label className="text-sm text-slate-400 block mb-2">Enter code to disable 2FA</label>
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="input-dark w-full"
                    data-testid="2fa-disable-code"
                  />
                </div>

                {twoFAError && (
                  <p className="text-red-400 text-sm" data-testid="2fa-disable-error">{twoFAError}</p>
                )}

                <button
                  onClick={disable2FA}
                  disabled={!twoFACode || twoFALoading}
                  className="btn-danger w-full"
                  data-testid="disable-2fa-btn"
                >
                  {twoFALoading ? "Processing..." : "Disable 2FA"}
                </button>
              </div>
            ) : twoFASetup ? (
              <div className="space-y-4 max-w-md">
                <p className="text-sm text-slate-400">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>

                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={twoFASetup.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
                </div>

                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Manual entry code:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-cyan-400 font-mono flex-1" data-testid="2fa-manual-secret">{twoFASetup.secret}</code>
                    <button
                      onClick={() => copyToClipboard(twoFASetup.secret)}
                      className="text-slate-400 hover:text-cyan-400"
                      data-testid="2fa-copy-secret-btn"
                      aria-label="Copy manual 2FA secret"
                      title="Copy manual 2FA secret"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-slate-400 block mb-2">Enter verification code</label>
                  <input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    className="input-dark w-full"
                    data-testid="2fa-verify-code"
                  />
                </div>

                {twoFAError && (
                  <p className="text-red-400 text-sm" data-testid="2fa-setup-error">{twoFAError}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => { setTwoFASetup(null); setTwoFACode(""); setTwoFAError(""); }}
                    className="btn-secondary flex-1"
                    data-testid="2fa-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={verify2FA}
                    disabled={!twoFACode || twoFALoading}
                    className="btn-primary flex-1"
                    data-testid="verify-2fa-btn"
                  >
                    {twoFALoading ? "Verifying..." : "Enable 2FA"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 max-w-md">
                <p className="text-sm text-slate-400">
                  Add an extra layer of security to your account by enabling two-factor authentication.
                </p>

                <div className="flex items-center gap-3 p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                  <Smartphone className="w-8 h-8 text-slate-500" />
                  <div>
                    <p className="text-sm text-slate-300">Authenticator App</p>
                    <p className="text-xs text-slate-500">Use Google Authenticator, Authy, or similar</p>
                  </div>
                </div>

                <button
                  onClick={setup2FA}
                  disabled={twoFALoading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="setup-2fa-btn"
                >
                  <QrCode className="w-4 h-4" />
                  {twoFALoading ? "Setting up..." : "Setup 2FA"}
                </button>
              </div>
            )}
          </div>

          {/* Change Password */}
          <div className="card-dark p-5 sm:p-6" data-testid="change-password">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" />
              Change Password
            </h3>

            <div className="space-y-4 max-w-md">
              <p className="text-sm text-slate-400">
                Update your password regularly to keep your account secure.
              </p>

              {/* Current Password */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    placeholder="Enter current password"
                    className="input-dark w-full pr-10"
                    data-testid="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="current-password-visibility-btn"
                    aria-label={showPasswords.current ? "Hide current password" : "Show current password"}
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    placeholder="Enter new password"
                    className="input-dark w-full pr-10"
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="new-password-visibility-btn"
                    aria-label={showPasswords.new ? "Hide new password" : "Show new password"}
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="text-sm text-slate-400 block mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    placeholder="Confirm new password"
                    className="input-dark w-full pr-10"
                    data-testid="confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="confirm-password-visibility-btn"
                    aria-label={showPasswords.confirm ? "Hide password confirmation" : "Show password confirmation"}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Password Message */}
              {passwordMessage.text && (
                <div className={`p-3 rounded-lg ${
                  passwordMessage.type === "success" 
                    ? "bg-green-500/10 border border-green-500/30 text-green-400" 
                    : "bg-red-500/10 border border-red-500/30 text-red-400"
                }`} data-testid="password-message" role={passwordMessage.type === "success" ? "status" : "alert"}>
                  {passwordMessage.text}
                </div>
              )}

              {/* Change Password Button */}
              <button
                onClick={changePassword}
                disabled={passwordSaving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="change-password-btn"
              >
                <Key className="w-4 h-4" />
                {passwordSaving ? "Changing..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* General Tab */}
      {activeTab === "general" && (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Redirector Settings */}
          <div className="card-dark p-6" data-testid="redirector-settings">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-cyan-400" />
                Redirector Settings
              </h3>
              {multiUrlEnabled && (
                <span
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-300"
                  data-testid="multi-url-badge"
                  title="Multi-URL Destinations entitlement is active for your account"
                >
                  Multi-URL Enabled
                </span>
              )}
            </div>

            <div className="space-y-4">
              {!multiUrlEnabled ? (
                <>
                  <div>
                    <label className="text-sm text-slate-400 block mb-2">Redirect URL</label>
                    <input
                      type="text"
                      value={settings.redirect_urls?.[0] || ""}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          redirect_url: e.target.value,
                          redirect_urls: [e.target.value],
                        })
                      }
                      placeholder="https://example.com"
                      className="input-dark w-full"
                      data-testid="redirect-url-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Need multiple destinations?{" "}
                    <span className="text-slate-400">
                      Ask an admin to enable the "Multi-URL Destinations" feature on your account.
                    </span>
                  </p>
                </>
              ) : (
                <div data-testid="multi-redirect-urls">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-slate-400">Redirect URLs</label>
                    <span className="text-xs text-slate-500" data-testid="multi-url-counter">
                      {(settings.redirect_urls || []).filter((u) => (u || "").trim()).length}/{maxRedirectUrls}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {(settings.redirect_urls && settings.redirect_urls.length > 0
                      ? settings.redirect_urls
                      : [""]
                    ).map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`redirect-url-row-${idx}`}>
                        <span className="text-xs text-slate-500 w-6 text-right select-none">{idx + 1}.</span>
                        <input
                          type="text"
                          value={url}
                          onChange={(e) => {
                            const next = [...(settings.redirect_urls || [])];
                            next[idx] = e.target.value;
                            setSettings({
                              ...settings,
                              redirect_urls: next,
                              redirect_url: next[0] || "",
                            });
                          }}
                          placeholder={idx === 0 ? "https://example.com (primary)" : "https://additional-destination.com"}
                          className="input-dark flex-1"
                          data-testid={`redirect-url-input-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const next = (settings.redirect_urls || []).filter((_, i) => i !== idx);
                            const finalList = next.length > 0 ? next : [""];
                            setSettings({
                              ...settings,
                              redirect_urls: finalList,
                              redirect_url: finalList[0] || "",
                            });
                          }}
                          disabled={(settings.redirect_urls || []).length <= 1}
                          className="p-2 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Remove this URL"
                          data-testid={`redirect-url-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const current = settings.redirect_urls || [];
                      if (current.length >= maxRedirectUrls) return;
                      setSettings({
                        ...settings,
                        redirect_urls: [...current, ""],
                      });
                    }}
                    disabled={(settings.redirect_urls || []).length >= maxRedirectUrls}
                    className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="redirect-url-add-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add destination URL
                  </button>

                  <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                    The first URL is the primary destination used by your generated links. Additional URLs are
                    available for advanced rotation on your redirect agent.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Link Expiry Settings */}
          <div className="card-dark p-6" data-testid="link-expiry-settings">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-400" />
              Link Expiry
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              Set how long links remain active. After expiry, visitors will see a blank page to prevent forensic analysis.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-2">Expire links after (hours)</label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="0"
                    max="8760"
                    value={settings.link_expiry_hours || 0}
                    onChange={(e) => setSettings({ ...settings, link_expiry_hours: parseInt(e.target.value) || 0 })}
                    className="input-dark w-32"
                    data-testid="link-expiry-input"
                  />
                  <span className="text-slate-400 text-sm">
                    {settings.link_expiry_hours === 0 ? "Never expires" : 
                     settings.link_expiry_hours === 1 ? "1 hour" :
                     settings.link_expiry_hours < 24 ? `${settings.link_expiry_hours} hours` :
                     settings.link_expiry_hours === 24 ? "1 day" :
                     settings.link_expiry_hours < 168 ? `${Math.round(settings.link_expiry_hours / 24)} days` :
                     settings.link_expiry_hours === 168 ? "1 week" :
                     `${Math.round(settings.link_expiry_hours / 168)} weeks`}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Set to 0 for links that never expire. Common values: 24 (1 day), 72 (3 days), 168 (1 week)
                </p>
              </div>
            </div>
          </div>

          <LandingTemplateList
            templates={landingPageTemplates}
            selectedId={settings.current_preview}
            activeId={savedLandingTemplateId}
            onSelect={selectLandingTemplate}
            onPreview={previewLandingTemplate}
            saving={saving}
            message={landingTemplateMessage}
          />

      </div>

      <SettingsSaveDock description="Save routing and link defaults" message={settingsMessage}>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="btn-primary flex h-10 items-center gap-2 px-5"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </SettingsSaveDock>
      </>
      )}


      {/* Security Tab */}
      {activeTab === "security" && (
        <div className="settings-security-grid">
          {/* Scanner/Bot Blocking - ALL scanner/bot toggles merged here */}
          <div className="card-dark p-5 sm:p-6" data-testid="scanner-protection-settings">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-400" />
              Scanner & Bot Protection
            </h3>
            <p className="text-sm text-slate-400 mb-4">Block security scanners to improve inbox delivery</p>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Block Security Scanners</p>
                  <p className="text-xs text-slate-500">SafeLinks, Proofpoint, Mimecast, Barracuda, etc.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSecuritySettings({...securitySettings, block_scanners: !securitySettings.block_scanners})}
                  className={`w-12 h-6 rounded-full transition-colors ${securitySettings.block_scanners ? "bg-green-500" : "bg-slate-700"}`}
                  data-testid="block-scanners-toggle"
                  aria-pressed={securitySettings.block_scanners}
                  aria-label="Toggle security scanner blocking"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${securitySettings.block_scanners ? "translate-x-6" : "translate-x-0.5"}`}></div>
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Block Datacenter IPs</p>
                  <p className="text-xs text-slate-500">AWS, Azure, GCP, DigitalOcean, etc.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSecuritySettings({...securitySettings, block_datacenter_ips: !securitySettings.block_datacenter_ips})}
                  className={`w-12 h-6 rounded-full transition-colors ${securitySettings.block_datacenter_ips ? "bg-green-500" : "bg-slate-700"}`}
                  data-testid="block-datacenter-ips-toggle"
                  aria-pressed={securitySettings.block_datacenter_ips}
                  aria-label="Toggle datacenter IP blocking"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${securitySettings.block_datacenter_ips ? "translate-x-6" : "translate-x-0.5"}`}></div>
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Enable Scanner Detection</p>
                  <p className="text-xs text-slate-500">Behavioral analysis for scanners</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="scanner-detection-toggle"
                    checked={advancedSecurity.scanner_detection_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, scanner_detection_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-red-500"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Block Security Scanners</p>
                  <p className="text-xs text-slate-500">ATP, Proofpoint, Mimecast, etc.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="advanced-scanner-block-toggle"
                    checked={advancedSecurity.block_security_scanners}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_security_scanners: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-red-500"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Block SEO Bots</p>
                  <p className="text-xs text-slate-500">Googlebot, Bingbot, etc.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="block-seo-bots-toggle"
                    checked={advancedSecurity.block_seo_bots}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_seo_bots: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-red-500"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Bot Redirect URL */}
          <div className="card-dark p-5 sm:p-6" data-testid="bot-redirect-settings">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-cyan-400" />
              Bot Redirect Destination
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              Instead of blocking bots with an error page, redirect them to a custom URL. Leave empty to show the default block page.
            </p>
            <div>
              <label className="text-sm text-slate-400 block mb-2">Redirect bots to URL</label>
              <input
                type="text"
                value={securitySettings.bot_redirect_url || ""}
                onChange={(e) => setSecuritySettings({...securitySettings, bot_redirect_url: e.target.value})}
                placeholder="https://google.com"
                className="input-dark w-full"
                data-testid="bot-redirect-url-input"
              />
              <p className="text-xs text-slate-500 mt-2">
                {securitySettings.bot_redirect_url
                  ? `Bots will be redirected to: ${securitySettings.bot_redirect_url}`
                  : "Bots will see the default block page"}
              </p>
            </div>
          </div>

          {/* SafeLinks Bypass - Only for blank redirects */}
          <div className="card-dark p-5 sm:p-6" data-testid="fallback-delay-settings">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              Fallback Delay (Blank Redirects Only)
            </h3>
            <p className="text-sm text-slate-400 mb-4">Only applies when using "Blank" landing page. Your custom landing pages already have delays built-in.</p>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Enable Fallback Delay</p>
                  <p className="text-xs text-slate-500">Add delay for blank/direct redirects (not needed with landing pages)</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSecuritySettings({...securitySettings, delayed_redirect: !securitySettings.delayed_redirect})}
                  className={`w-12 h-6 rounded-full transition-colors ${securitySettings.delayed_redirect ? "bg-green-500" : "bg-slate-700"}`}
                  data-testid="fallback-delay-toggle"
                  aria-pressed={securitySettings.delayed_redirect}
                  aria-label="Toggle fallback delay"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${securitySettings.delayed_redirect ? "translate-x-6" : "translate-x-0.5"}`}></div>
                </button>
              </div>

              {securitySettings.delayed_redirect && (
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-xs text-yellow-400 mb-2">Only enable if you're using "Blank" landing page without custom delay</p>
                  <label className="text-sm text-slate-400 block mb-2">Delay Duration (seconds)</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={securitySettings.delay_seconds}
                    onChange={(e) => setSecuritySettings({...securitySettings, delay_seconds: parseInt(e.target.value) || 3})}
                    className="input-dark w-24"
                    data-testid="fallback-delay-seconds-input"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Additional Security */}
          <div className="card-dark p-5 sm:p-6" data-testid="additional-protection-settings">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-400" />
              Additional Protection
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Require Email Client Referrer</p>
                  <p className="text-xs text-slate-500">Only allow clicks from Outlook, Gmail, etc. (Strict Mode)</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSecuritySettings({...securitySettings, require_referrer: !securitySettings.require_referrer})}
                  className={`w-12 h-6 rounded-full transition-colors ${securitySettings.require_referrer ? "bg-yellow-500" : "bg-slate-700"}`}
                  data-testid="require-referrer-toggle"
                  aria-pressed={securitySettings.require_referrer}
                  aria-label="Toggle email client referrer requirement"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${securitySettings.require_referrer ? "translate-x-6" : "translate-x-0.5"}`}></div>
                </button>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">CAPTCHA Challenge</p>
                  <p className="text-xs text-slate-500">Show human verification before redirect</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSecuritySettings({...securitySettings, enable_captcha: !securitySettings.enable_captcha})}
                  className={`w-12 h-6 rounded-full transition-colors ${securitySettings.enable_captcha ? "bg-purple-500" : "bg-slate-700"}`}
                  data-testid="captcha-toggle"
                  aria-pressed={securitySettings.enable_captcha}
                  aria-label="Toggle CAPTCHA challenge"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${securitySettings.enable_captcha ? "translate-x-6" : "translate-x-0.5"}`}></div>
                </button>
              </div>
            </div>
          </div>

          {/* Domain Rotation Settings */}
          <div className="card-dark p-5 sm:p-6" data-testid="rotation-settings">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-cyan-400" />
              Smart Domain Rotation
            </h3>
            <p className="text-sm text-slate-400 mb-4">Automatically rotate domains to prevent burning</p>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-white font-medium">Enable Auto-Rotation</p>
                  <p className="text-xs text-slate-500">Rotate domains after max clicks reached</p>
                </div>
                <button
                  type="button"
                  onClick={() => setRotationSettings({...rotationSettings, enabled: !rotationSettings.enabled})}
                  className={`w-12 h-6 rounded-full transition-colors ${rotationSettings.enabled ? "bg-cyan-500" : "bg-slate-700"}`}
                  data-testid="rotation-enabled-toggle"
                  aria-pressed={rotationSettings.enabled}
                  aria-label="Toggle domain rotation"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${rotationSettings.enabled ? "translate-x-6" : "translate-x-0.5"}`}></div>
                </button>
              </div>

              {rotationSettings.enabled && (
                <>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <label className="text-sm text-slate-400 block mb-2">Max Clicks Per Domain</label>
                    <input
                      type="number"
                      min="100"
                      max="10000"
                      value={rotationSettings.max_clicks_per_domain}
                      onChange={(e) => setRotationSettings({...rotationSettings, max_clicks_per_domain: parseInt(e.target.value) || 2500})}
                      className="input-dark w-24"
                      data-testid="rotation-max-clicks-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Recommended: 1000-5000 clicks</p>
                  </div>

                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <label className="text-sm text-slate-400 block mb-2">Cooldown Period (hours)</label>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={rotationSettings.cooldown_hours}
                      onChange={(e) => setRotationSettings({...rotationSettings, cooldown_hours: parseInt(e.target.value) || 24})}
                      className="input-dark w-24"
                      data-testid="rotation-cooldown-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Rest burned domains before reuse</p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="text-sm text-white font-medium">Auto-Disable Burned Domains</p>
                      <p className="text-xs text-slate-500">Automatically disable domains that exceed limits</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRotationSettings({...rotationSettings, auto_disable_burned: !rotationSettings.auto_disable_burned})}
                      className={`w-12 h-6 rounded-full transition-colors ${rotationSettings.auto_disable_burned ? "bg-red-500" : "bg-slate-700"}`}
                      data-testid="rotation-auto-disable-toggle"
                      aria-pressed={rotationSettings.auto_disable_burned}
                      aria-label="Toggle automatic burned-domain disable"
                    >
                      <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${rotationSettings.auto_disable_burned ? "translate-x-6" : "translate-x-0.5"}`}></div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
          <SettingsSaveDock description="Save security filters and domain rotation">
            <button
              onClick={saveRotationSettings}
              disabled={saving}
              className="btn-secondary flex h-10 items-center gap-2 px-4"
              data-testid="save-rotation-settings-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Save Rotation
            </button>
            <button
              onClick={() => { saveSecuritySettings(); saveAdvancedSecurity(); }}
              disabled={saving}
              className="btn-primary flex h-10 items-center gap-2 px-4"
              data-testid="save-security-settings-btn"
            >
              <Shield className="w-4 h-4" />
              {saving ? "Saving..." : "Save Security & Evasion"}
            </button>
          </SettingsSaveDock>
          </div>
        </div>
      )}



      {/* Advanced Security Tab */}
      {activeTab === "advanced" && (
        <div className="settings-advanced-grid">
          {/* Device Fingerprint Dashboard */}
          <div className="card-dark p-5 sm:p-6 lg:col-span-2" data-testid="fingerprint-dashboard">
            <div className="flex items-center gap-2 mb-4">
              <Smartphone className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-semibold">Device Fingerprint Dashboard</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Track unique devices visiting your links to detect repeat visitors and suspicious patterns.
            </p>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-400" data-testid="fingerprint-unique-count">{fingerprintStats.unique?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-500">Unique Devices</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-cyan-400" data-testid="fingerprint-returning-count">{fingerprintStats.returning?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-500">Returning Visitors</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-400" data-testid="fingerprint-suspicious-count">{fingerprintStats.suspicious?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-500">Suspicious Devices</p>
              </div>
            </div>
            
            <p className="text-xs text-slate-600 mt-4 text-center">
              Stats are collected locally on the agent server and updated in real-time
            </p>
          </div>

          {/* Click Velocity Limits */}
          <div className="card-dark p-5 sm:p-6" data-testid="velocity-settings">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-yellow-400" />
              <h2 className="text-lg font-semibold">Click Velocity Limits</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Limit clicks per IP to detect and block bot attacks and automated scanning.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Velocity Limiting</p>
                  <p className="text-sm text-slate-500">Auto-block IPs exceeding click limits</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="velocity-limiting-toggle"
                    checked={advancedSecurity.click_velocity_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, click_velocity_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-yellow-500"></div>
                </label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Max Clicks Per Minute</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={advancedSecurity.max_clicks_per_minute}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, max_clicks_per_minute: parseInt(e.target.value) || 10 }))}
                    className="input-dark w-full"
                    data-testid="max-clicks-minute-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Max Clicks Per Hour</label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={advancedSecurity.max_clicks_per_hour}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, max_clicks_per_hour: parseInt(e.target.value) || 100 }))}
                    className="input-dark w-full"
                    data-testid="max-clicks-hour-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Action When Exceeded</label>
                <select 
                  className="input-dark w-full" 
                  value={advancedSecurity.velocity_action}
                  onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, velocity_action: e.target.value }))}
                  data-testid="velocity-action-select"
                >
                  <option value="block">Block IP</option>
                  <option value="challenge">Show Challenge</option>
                  <option value="log">Log Only</option>
                </select>
              </div>
            </div>
          </div>

          {/* VPN/Proxy Detection */}
          <div className="card-dark p-5 sm:p-6" data-testid="vpn-proxy-settings">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-orange-400" />
              <h2 className="text-lg font-semibold">VPN/Proxy Detection</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Detect and block visitors using VPNs, proxies, or Tor to hide their identity.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable VPN/Proxy Detection</p>
                  <p className="text-sm text-slate-500">Block anonymized connections</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="vpn-detection-toggle"
                    checked={advancedSecurity.vpn_detection_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, vpn_detection_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-orange-500"></div>
                </label>
              </div>
              
              {advancedSecurity.vpn_detection_enabled && (
                <>
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block VPN Connections</p>
                      <p className="text-sm text-slate-500">Block known VPN provider IPs</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-vpn-toggle"
                        checked={advancedSecurity.block_vpn}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_vpn: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-orange-500"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Proxy Connections</p>
                      <p className="text-sm text-slate-500">Block HTTP/SOCKS proxies</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-proxy-toggle"
                        checked={advancedSecurity.block_proxy}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_proxy: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-orange-500"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Tor Exit Nodes</p>
                      <p className="text-sm text-slate-500">Block all Tor network traffic</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-tor-toggle"
                        checked={advancedSecurity.block_tor}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_tor: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-orange-500"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Datacenter IPs</p>
                      <p className="text-sm text-slate-500">Block cloud/hosting provider IPs</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-advanced-datacenter-toggle"
                        checked={advancedSecurity.block_datacenter}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_datacenter: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-orange-500"></div>
                    </label>
                  </div>

                </>
              )}
            </div>
          </div>

          {/* Browser Fingerprint Blocking */}
          <div className="card-dark p-5 sm:p-6" data-testid="browser-fingerprint-settings">
            <div className="flex items-center gap-2 mb-4">
              <Monitor className="w-5 h-5 text-indigo-400" />
              <h2 className="text-lg font-semibold">Browser Fingerprint Blocking</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Detect and block headless browsers and automation tools used by scanners.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Fingerprint Blocking</p>
                  <p className="text-sm text-slate-500">Block suspicious browser fingerprints</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="fingerprint-blocking-toggle"
                    checked={advancedSecurity.fingerprint_blocking_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, fingerprint_blocking_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-indigo-500"></div>
                </label>
              </div>
              
              {advancedSecurity.fingerprint_blocking_enabled && (
                <>
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Headless Browsers</p>
                      <p className="text-sm text-slate-500">Puppeteer, Playwright, Selenium</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-headless-toggle"
                        checked={advancedSecurity.block_headless}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_headless: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-indigo-500"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Automation Tools</p>
                      <p className="text-sm text-slate-500">WebDriver, PhantomJS detection</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-automation-toggle"
                        checked={advancedSecurity.block_automation}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_automation: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-indigo-500"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Inconsistent Fingerprints</p>
                      <p className="text-sm text-slate-500">Detect spoofed browser properties</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-inconsistent-fingerprint-toggle"
                        checked={advancedSecurity.block_inconsistent_fp}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_inconsistent_fp: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-indigo-500"></div>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Geographic Anomaly Detection */}
          <div className="card-dark p-5 sm:p-6" data-testid="geo-anomaly-settings">
            <div className="flex items-center gap-2 mb-4">
              <Map className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-semibold">Geographic Anomaly Detection</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Detect "impossible travel" - when the same user appears from different countries in a short time.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Geo Anomaly Detection</p>
                  <p className="text-sm text-slate-500">Flag suspicious geographic jumps</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="geo-anomaly-toggle"
                    checked={advancedSecurity.geo_anomaly_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, geo_anomaly_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-emerald-500"></div>
                </label>
              </div>
              
              {advancedSecurity.geo_anomaly_enabled && (
                <>
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Max Travel Speed (km/h)</label>
                    <input
                      type="number"
                      min="100"
                      max="2000"
                      value={advancedSecurity.impossible_travel_threshold}
                      onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, impossible_travel_threshold: parseInt(e.target.value) || 500 }))}
                      className="input-dark w-32"
                      data-testid="impossible-travel-threshold-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Flights ~900 km/h. Faster = impossible</p>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Require Consistent Country</p>
                      <p className="text-sm text-slate-500">Block if country changes for same email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="consistent-country-toggle"
                        checked={advancedSecurity.require_consistent_country}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, require_consistent_country: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* TLS/JA3 Fingerprinting */}
          <div className="card-dark p-5 sm:p-6" data-testid="tls-fingerprint-settings">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-pink-400" />
              <h2 className="text-lg font-semibold">TLS/JA3 Fingerprinting</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Detect bots by analyzing their TLS handshake signature.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable TLS Fingerprinting</p>
                  <p className="text-sm text-slate-500">Analyze TLS client signatures</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="tls-fingerprinting-toggle"
                    checked={advancedSecurity.tls_fingerprinting_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, tls_fingerprinting_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-pink-500"></div>
                </label>
              </div>
              
              {advancedSecurity.tls_fingerprinting_enabled && (
                <>
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Known Bot JA3</p>
                      <p className="text-sm text-slate-500">Pre-built scanner signatures</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-known-ja3-toggle"
                        checked={advancedSecurity.block_known_bot_ja3}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_known_bot_ja3: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-pink-500"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium">Block Suspicious TLS</p>
                      <p className="text-sm text-slate-500">Unusual cipher suites/versions</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        data-testid="block-suspicious-tls-toggle"
                        checked={advancedSecurity.block_suspicious_tls}
                        onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, block_suspicious_tls: e.target.checked }))}
                      />
                      <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-pink-500"></div>
                    </label>
                  </div>

                </>
              )}
            </div>
          </div>

          {/* Honeypot Links */}
          <div className="card-dark p-5 sm:p-6" data-testid="honeypot-settings">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold">Honeypot Links</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Create decoy links to detect scrapers and bots.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Honeypot Detection</p>
                  <p className="text-sm text-slate-500">Auto-block IPs that visit honeypot links</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="honeypot-toggle"
                    checked={advancedSecurity.honeypot_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, honeypot_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-amber-500"></div>
                </label>
              </div>
              
              {advancedSecurity.honeypot_enabled && (
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="font-medium">Auto-Block on Honeypot Visit</p>
                    <p className="text-sm text-slate-500">Instantly block IPs that hit honeypots</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      data-testid="honeypot-auto-block-toggle"
                      checked={advancedSecurity.honeypot_auto_block}
                      onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, honeypot_auto_block: e.target.checked }))}
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-amber-500"></div>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* IP Reputation Scoring */}
          <div className="card-dark p-5 sm:p-6" data-testid="reputation-settings">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-green-400" />
              <h2 className="text-lg font-semibold">IP Reputation Scoring</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Score IPs based on behavior and block suspicious ones.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Reputation Scoring</p>
                  <p className="text-sm text-slate-500">Auto-block low reputation IPs</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="reputation-scoring-toggle"
                    checked={advancedSecurity.reputation_scoring_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, reputation_scoring_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-green-500"></div>
                </label>
              </div>
              
              {advancedSecurity.reputation_scoring_enabled && (
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Block Threshold (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={advancedSecurity.reputation_threshold}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, reputation_threshold: parseInt(e.target.value) || 30 }))}
                    className="input-dark w-32"
                    data-testid="reputation-threshold-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">IPs below this score will be blocked</p>
                </div>
              )}
            </div>
          </div>

          {/* Time-Delayed Activation */}
          <div className="card-dark p-5 sm:p-6" data-testid="delayed-activation-settings">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-semibold">Time-Delayed Activation</h2>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Delay link activation to evade scanner timing patterns.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Delayed Activation</p>
                  <p className="text-sm text-slate-500">Links become active after a delay</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    data-testid="time-delay-toggle"
                    checked={advancedSecurity.time_delay_enabled}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, time_delay_enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-blue-500"></div>
                </label>
              </div>
              
              {advancedSecurity.time_delay_enabled && (
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Delay (minutes)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={advancedSecurity.delay_minutes}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, delay_minutes: parseInt(e.target.value) || 5 }))}
                    className="input-dark w-32"
                    data-testid="time-delay-minutes-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Visitors before this time see safe page</p>
                </div>
              )}
            </div>
          </div>

          {/* Next-Gen Bot Defense (Session 10) */}
          <div className="card-dark border border-cyan-500/20 p-5 sm:p-6 lg:col-span-2" data-testid="nextgen-defense-card">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-semibold">Next-Gen Bot Defense</h2>
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">New</span>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Extra layers that run on your redirect agent. Toggle per-account; leave off if you're unsure.
            </p>

            <div className="space-y-3">
              {[
                { key: "enable_ja3", title: "TLS JA3/JA4 Fingerprint Blocking", desc: "Blocks python/curl/Go clients by TLS handshake. Requires the fingerproxy component in front of Nginx." },
                { key: "enable_header_checks", title: "HTTP Header Anomaly Checks", desc: "Flags browsers missing Accept-Language, Sec-Fetch-* and other real-browser headers." },
                { key: "enable_behavioral", title: "Behavioral JS Gate", desc: "Invisible pointer/timing check (~0.7s) before the redirect. Headless bots fail it." },
                { key: "enable_crawler_cidr", title: "Published-Crawler IP Auto-Block", desc: "Daily-refreshed Google/Bing/Facebook/AV crawler IP ranges are blocked." },
                { key: "enable_fp_velocity", title: "Fingerprint Velocity", desc: "Blocks one fingerprint appearing from many countries/ASNs in minutes (proxy rotation)." },
                { key: "enable_geo_consistency", title: "Geo Consistency", desc: "Blocks when the browser timezone/locale contradicts the IP country (datacenter UTC default)." },
                { key: "second_hop_cloak", title: "Second-Hop Cloak", desc: "Real destination is only revealed after the behavioral gate passes. Needs the Behavioral JS Gate on." },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div className="pr-4">
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      data-testid={`toggle-${item.key}`}
                      checked={!!advancedSecurity[item.key]}
                      onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, [item.key]: e.target.checked }))}
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:bg-cyan-500"></div>
                  </label>
                </div>
              ))}

              {advancedSecurity.enable_ja3 && (
                <div className="space-y-3 rounded-md border border-slate-700 bg-slate-800/40 p-4" data-testid="ja3-denylist-block">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <label htmlFor="ja3-denylist-input" className="block text-sm font-medium text-slate-300">Blocked JA3 hashes</label>
                      <p className="mt-1 text-xs text-slate-500">One confirmed 32-character MD5 fingerprint per line</p>
                    </div>
                    <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-xs text-cyan-300" data-testid="ja3-denylist-count">{(advancedSecurity.ja3_denylist || []).length} hashes</span>
                  </div>
                  {(advancedSecurity.ja3_denylist || []).length === 0 && (
                    <div className="flex flex-col gap-3 rounded-md border border-dashed border-slate-600 p-4 text-center" data-testid="ja3-denylist-empty-state">
                      <div>
                        <p className="text-sm font-medium text-slate-300">No custom TLS fingerprints are blocked yet.</p>
                        <p className="mt-1 text-xs text-slate-500">Built-in mismatch detection remains active while this list is empty.</p>
                      </div>
                      <button type="button" onClick={() => document.getElementById("ja3-denylist-input")?.focus()} data-testid="ja3-denylist-add-first-btn" className="mx-auto rounded-md border border-cyan-500/40 px-3 py-2 text-sm text-cyan-300 hover:bg-cyan-500/10">Add first fingerprint</button>
                    </div>
                  )}
                  <textarea
                    id="ja3-denylist-input"
                    rows={3}
                    value={(advancedSecurity.ja3_denylist || []).join("\n")}
                    onChange={(e) => setAdvancedSecurity(prev => ({ ...prev, ja3_denylist: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) }))}
                    className="input-dark w-full font-mono text-xs"
                    placeholder="e7d705a3286e19ea42f587b344ee6865"
                    data-testid="ja3-denylist-input"
                  />
                  <p className="text-xs text-slate-500">Add confirmed bot JA3 hashes from your agent logs. Leave empty to rely on the UA/JA4 mismatch heuristic.</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
          <SettingsSaveDock description="Save and push advanced defenses to agents" message={advancedSecurityMessage}>
            <button
              onClick={saveAdvancedSecurity}
              disabled={advancedSecuritySaving}
              data-testid="save-advanced-security-btn"
              className="btn-primary flex h-10 items-center gap-2 px-5"
            >
              <Shield className="w-4 h-4" />
              {advancedSecuritySaving ? "Saving..." : "Save Advanced Security"}
            </button>
          </SettingsSaveDock>
          </div>
        </div>
      )}

          </div>
        </section>
      </div>

      <PreviewDialog
        open={landingPreview.open}
        onOpenChange={(open) => setLandingPreview((current) => ({ ...current, open }))}
        title={`${landingPreview.template?.name || "Landing page"} preview`}
        description="Preview only. Scripts, forms, and navigation are disabled."
        html={landingPreview.html}
        loading={landingPreview.loading}
        error={landingPreview.error}
        testIdPrefix="landing-preview"
      />

    </div>
  );
};

export default Settings;
